import { useEffect, useRef } from 'react';

import { ensureRealtimeConnected, getRealtimeSocket, onRealtimeSocketOpen } from 'api/realtime';

// Eventos emitidos pelo backend (NotificationsService) para o usuário dono do
// socket. O contador de não lidas chega SEMPRE como valor absoluto, então
// aplicá-lo é idempotente (não acumula com updates otimistas locais).
type UnreadCountPayload = { count: number };
type NewNotificationPayload = {
  id?: string;
  title?: string;
  message?: string;
  entity?: string | null;
  registerId?: string | null;
  read?: boolean;
  createdAt?: string;
};
type UpdatedPayload = { id?: string; read?: boolean; readAt?: string | null };
type DeletedPayload = { id?: string };
type AllReadPayload = { affected?: number };

type Options = {
  enabled?: boolean;
  /** Novo valor (absoluto) do contador de não lidas. */
  onUnreadCount?: (count: number) => void;
  /** Uma notificação nova chegou. */
  onNew?: (data: NewNotificationPayload) => void;
  onUpdated?: (data: UpdatedPayload) => void;
  onDeleted?: (data: DeletedPayload) => void;
  onAllRead?: (data: AllReadPayload) => void;
  /** Disparado a cada (re)conexão do socket — use para ressincronizar o estado. */
  onReconnect?: () => void;
};

/**
 * Assina os eventos de notificação em tempo real (WebSocket). Substitui o
 * polling: o contador da bolinha atualiza no instante em que o backend emite,
 * sem esperar intervalo. Segue o mesmo padrão de useWhatsAppRealtime.
 */
export function useNotificationsRealtime({
  enabled = true,
  onUnreadCount,
  onNew,
  onUpdated,
  onDeleted,
  onAllRead,
  onReconnect
}: Options) {
  const handlersRef = useRef({ onUnreadCount, onNew, onUpdated, onDeleted, onAllRead, onReconnect });
  handlersRef.current = { onUnreadCount, onNew, onUpdated, onDeleted, onAllRead, onReconnect };

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let teardown: (() => void) | undefined;

    (async () => {
      await ensureRealtimeConnected();
      if (cancelled) return;

      const socket = getRealtimeSocket();

      const unreadHandler = (data: UnreadCountPayload) => {
        if (typeof data?.count === 'number') handlersRef.current.onUnreadCount?.(data.count);
      };
      const newHandler = (data: NewNotificationPayload) => handlersRef.current.onNew?.(data);
      const updatedHandler = (data: UpdatedPayload) => handlersRef.current.onUpdated?.(data);
      const deletedHandler = (data: DeletedPayload) => handlersRef.current.onDeleted?.(data);
      const allReadHandler = (data: AllReadPayload) => handlersRef.current.onAllRead?.(data);

      socket.on('notification:unread_count', unreadHandler);
      socket.on('notification:new', newHandler);
      socket.on('notification:updated', updatedHandler);
      socket.on('notification:deleted', deletedHandler);
      socket.on('notification:all_read', allReadHandler);

      // Ressincroniza ao (re)conectar — cobre eventos perdidos durante quedas de
      // conexão. É um disparo pontual por evento, não um intervalo.
      const offOpen = onRealtimeSocketOpen(() => handlersRef.current.onReconnect?.());

      teardown = () => {
        socket.off('notification:unread_count', unreadHandler);
        socket.off('notification:new', newHandler);
        socket.off('notification:updated', updatedHandler);
        socket.off('notification:deleted', deletedHandler);
        socket.off('notification:all_read', allReadHandler);
        offOpen();
      };
    })();

    return () => {
      cancelled = true;
      teardown?.();
    };
  }, [enabled]);
}
